import client from "../client";
import { formatErrorMessage } from "src/utils/misc";
import { mockWorkOrders, mockWorkOrdersByVehicle } from "./mockWorkOrders";
import { mockWorkshopWorkOrders } from "./mockWorkshopWorkOrders";
import { mockVehicles } from "./mockVehicles";
import { rememberPlateNumbers } from "./plateNumberCache";
import { mockVehicleIssues } from "./mockVehicleIssues";
import { mockWorkOrderDetails } from "./mockWorkOrderDetails";

export async function getWorkshopWorkOrders( // NOSONAR
	params: {
		status?: string;
		page?: number;
		pageSize?: number;
	} = {}
) {
	const items = params.status
		? mockWorkshopWorkOrders.filter(
				(workOrder) => workOrder.status === params.status
			)
		: mockWorkshopWorkOrders;

	return {
		data: {
			items,
			page: 1,
			pageSize: items.length,
			totalCount: items.length,
		},
	};
}

// PATCH api/fmstasking/v1/mr/workshop/work-orders/{id}/complete (EL-1221).
export async function completeWorkshopWorkOrder( // NOSONAR
	workOrderId,
	{ returnToHomeLot = false, issues = [] }: any = {}
) {
	const workOrder = mockWorkOrderDetails.find(
		({ id }) => id === Number(workOrderId)
	);
	if (!workOrder) {
		return { error: { status: 404, code: 29201 } };
	}

	if (workOrder.status !== "InWorkshop") {
		return { error: { status: 403, code: 29401 } };
	}

	const liveIssueIds = (workOrder.issues || []).map((issue) => issue.id);
	const liveIssueIdSet = new Set(liveIssueIds);
	const submittedIssueIds = new Set(issues.map((issue) => issue.issueId));
	const missingIssueIds = liveIssueIds.filter(
		(id) => !submittedIssueIds.has(id)
	);
	if (missingIssueIds.length) {
		return {
			error: { status: 422, code: 29405, issueIds: missingIssueIds },
		};
	}

	const extraneousIssueIds = issues
		.map((issue) => issue.issueId)
		.filter((id) => !liveIssueIdSet.has(id));
	if (extraneousIssueIds.length) {
		return {
			error: { status: 422, code: 29405, issueIds: extraneousIssueIds },
		};
	}

	const missingReasonIssueIds = issues
		.filter(
			(issue) => issue.outcome === "NotFixed" && !issue.notFixedReason
		)
		.map((issue) => issue.issueId);
	if (missingReasonIssueIds.length) {
		return {
			error: {
				status: 422,
				code: 29402,
				issueIds: missingReasonIssueIds,
			},
		};
	}

	const workshopCompletedAt = new Date().toISOString();
	const newStatus = returnToHomeLot
		? "PendingCompletion"
		: "CompletedByWorkshop";
	const newAllowedTransitions = allowedTransitionsFor(
		newStatus,
		workOrder.flowType
	);

	const nextStep = returnToHomeLot
		? "Workshop will move the vehicle back to its home lot"
		: "Vehicle is ready to be collected";

	issues.forEach(({ issueId, outcome, notFixedReason }) => {
		const unfixedReason = outcome === "NotFixed" ? notFixedReason : null;
		const nestedIssue = (workOrder.issues || []).find(
			(issue) => issue.id === issueId
		);
		if (nestedIssue) {
			nestedIssue.outcome = outcome;
			nestedIssue.unfixedReason = unfixedReason;
		}
		const fullIssue = mockVehicleIssues.find(
			(issue) => issue.id === issueId
		);
		if (fullIssue) {
			fullIssue.outcome = outcome;
			fullIssue.unfixedReason = unfixedReason;
		}
	});

	workOrder.status = newStatus;
	workOrder.allowedTransitions = newAllowedTransitions;
	workOrder.workshopCompletedAt = workshopCompletedAt;
	workOrder.lastUpdatedAt = workshopCompletedAt;
	(workOrder as any).nextStep = nextStep;

	syncFlatWorkOrderStatus(workOrder.id, newStatus, newAllowedTransitions);

	const workshopListEntry = mockWorkshopWorkOrders.find(
		({ id }) => id === workOrder.id
	);
	if (workshopListEntry) {
		workshopListEntry.status = newStatus;
		workshopListEntry.updatedAt = workshopCompletedAt;
		issues.forEach(({ issueId, outcome, notFixedReason }) => {
			const listIssue = workshopListEntry.issues.find(
				(issue) => issue.id === issueId
			);
			if (listIssue) {
				listIssue.outcome = outcome;
				listIssue.unfixedReason =
					outcome === "NotFixed" ? notFixedReason : null;
			}
		});
	}

	return {
		data: {
			id: workOrder.id,
			status: newStatus,
			allowedTransitions: newAllowedTransitions,
			workshopCompletedAt,
			nextStep,
			issues: issues.map(({ issueId, outcome, notFixedReason }) => ({
				issueId,
				outcome,
				notFixedReason: outcome === "NotFixed" ? notFixedReason : "",
				resolvedAt: workshopCompletedAt,
			})),
		},
	};
}

export async function getMaintenanceRepairVehicles(
	params: {
		page?: number;
		size?: number;
		plateNumber?: string;
		lastUpdatedFrom?: string;
		lastUpdatedTo?: string;
		platform?: string;
		vehicleStatus?: string;
		handoverFlagged?: boolean;
		readyForCollection?: boolean;
		sortCol?: string;
		sortDir?: string;
	} = {}
) {
	const res = await client("/v1/mr/maintenance/vehicles", {
		method: "GET",
		version: "Tasking",
		params,
	});
	rememberPlateNumbers(res?.data?.items);
	return res;
}

// GET api/fmstasking/v1/mr/maintenance/vehicles/{vehicleId} (EL-1204)
export async function getMaintenanceRepairVehicleById(vehicleId) {
	const res = await client(`/v1/mr/maintenance/vehicles/${vehicleId}`, {
		method: "GET",
		version: "Tasking",
	});
	rememberPlateNumbers([res.data]);
	return res;
}

// TODO: swap this mock for a real `client()` call to
// `api/fmstasking/v1/maintenance/vehicles/{vehicleId}`.
// The workshop route reuses this same :vehicleId segment with a plate
// number instead — accept either.
export async function getVehicleById(vehicleId) /* NOSONAR */ {
	return {
		data: mockVehicles.find(
			(vehicle) =>
				String(vehicle.vehicleId) === String(vehicleId) ||
				vehicle.plateNumber === vehicleId
		),
	};
}

function isWithinDateRange(isoString, fromDate, toDate) {
	const time = new Date(isoString).getTime();
	return (
		time >= new Date(`${fromDate}T00:00:00`).getTime() &&
		time <= new Date(`${toDate}T23:59:59.999`).getTime()
	);
}

export async function getWorkOrders(vehicleId, params: any = {}) /* NOSONAR */ {
	const {
		code,
		status,
		addedByEmail,
		dateCreatedFrom,
		dateCreatedTo,
		lastUpdatedFrom,
		lastUpdatedTo,
		sortCol,
		sortDir,
	} = params;

	let items = (mockWorkOrdersByVehicle[Number(vehicleId)] ?? [])
		.filter((order) =>
			code ? order.code.toLowerCase().includes(code.toLowerCase()) : true
		)
		.filter((order) =>
			addedByEmail
				? (order.addedBy || "")
						.toLowerCase()
						.includes(addedByEmail.toLowerCase())
				: true
		)
		.filter((order) => (status ? order.status === status : true))
		.filter((order) =>
			dateCreatedFrom && dateCreatedTo
				? isWithinDateRange(
						order.createdAt,
						dateCreatedFrom,
						dateCreatedTo
					)
				: true
		)
		.filter((order) =>
			lastUpdatedFrom && lastUpdatedTo
				? isWithinDateRange(
						order.lastUpdatedAt,
						lastUpdatedFrom,
						lastUpdatedTo
					)
				: true
		);

	if (sortCol) {
		items = [...items].sort((a, b) => {
			const aVal = a[sortCol];
			const bVal = b[sortCol];
			let result = 0;
			if (aVal < bVal) result = -1;
			else if (aVal > bVal) result = 1;
			return sortDir === "descending" ? -result : result;
		});
	}

	return {
		data: {
			items,
			totalCount: items.length,
		},
	};
}

// GET api/fmstasking/v1/mr/vehicles/{vehicleId}/issues (EL-1201)
export async function getVehicleIssues(vehicleId, params: any = {}) {
	const res = await client(`/v1/mr/vehicles/${vehicleId}/issues`, {
		method: "GET",
		version: "Tasking",
		params,
	});
	const body = res.data ?? {};
	return {
		data: {
			items: body.data ?? [],
			page: body.pageNumber,
			pageSize: body.pageSize,
			totalCount: body.totalCount ?? 0,
		},
	};
}

// GET api/fmstasking/v1/mr/issues/{id} (EL-1183)
export async function getIssueById(issueId) {
	return client(`/v1/mr/issues/${issueId}`, {
		method: "GET",
		version: "Tasking",
	});
}

// TODO: swap this mock for a real `client()` call to
// `api/fmstasking/v1/vehicles/{vehicleId}/issues/{issueId}`.
export async function getVehicleIssueById(issueId, vehicleId) /* NOSONAR */ {
	// Same plate-number-as-vehicleId accommodation as getVehicleById above.
	const vehicle = mockVehicles.find(
		(v) =>
			String(v.vehicleId) === String(vehicleId) ||
			v.plateNumber === vehicleId
	);
	const issue = mockVehicleIssues.find(
		({ id, vehicleId: ownerVehicleId }) =>
			id === Number(issueId) &&
			(ownerVehicleId === Number(vehicleId) ||
				ownerVehicleId === vehicle?.vehicleId)
	);
	return { data: issue };
}

// TODO: swap this mock for a real `client()` call to
// `api/fmstasking/v1/vehicles/{vehicleId}/work-orders?code={search}`.
export async function getVehicleWorkOrders(vehicleId, search) /* NOSONAR */ {
	const items = mockWorkOrders
		.filter(
			(workOrder) => String(workOrder.vehicleId) === String(vehicleId)
		)
		.filter((workOrder) =>
			search
				? workOrder.code.toLowerCase().includes(search.toLowerCase())
				: true
		)
		.map((workOrder) => ({ value: workOrder.id, label: workOrder.code }));

	return items;
}

const toWireCategory = (code = "") =>
	code.replace("Non-workshop", "NonWorkshop");

// GET api/fmstasking/v1/mr/maintenance/issue-types?category={category}
// Cached per category; failures resolve to [] so forms stay usable.
const issueTypesCache = new Map();
export function getIssueTypes(category = "") {
	if (!issueTypesCache.has(category)) {
		const request = client("/v1/mr/maintenance/issue-types", {
			method: "GET",
			version: "Tasking",
			params: category
				? { category: toWireCategory(category) }
				: undefined,
		})
			.then((res) =>
				(res.data?.items ?? []).map((type) => ({
					value: type.name,
					label: type.name,
					id: type.id,
				}))
			)
			.catch(() => {
				issueTypesCache.delete(category);
				return [];
			});
		issueTypesCache.set(category, request);
	}
	return issueTypesCache.get(category);
}

// POST api/fmstasking/v1/mr/vehicles/{vehicleId}/issues (EL-1168).
export function createVehicleIssue(vehicleId, payload) {
	return client(`/v1/mr/vehicles/${vehicleId}/issues`, {
		method: "POST",
		version: "Tasking",
		data: {
			...payload,
			serviceCategory: toWireCategory(payload.serviceCategory),
		},
	});
}

// PUT api/fmstasking/v1/mr/issues/{id} (EL-1176)
export function updateVehicleIssue(issueId, payload) {
	return client(`/v1/mr/issues/${issueId}`, {
		method: "PUT",
		version: "Tasking",
		data: {
			...payload,
			serviceCategory: toWireCategory(payload.serviceCategory),
		},
	});
}

// PATCH api/fmstasking/v1/mr/issues/{id}/status (EL-1177)
export async function changeIssueStatus(
	issueId,
	{ currentState, targetState, reasonCode = undefined }
) {
	try {
		return await client(`/v1/mr/issues/${issueId}/status`, {
			method: "PATCH",
			version: "Tasking",
			data: { currentState, targetState, reasonCode },
		});
	} catch (error) {
		return {
			error: {
				status: error.response?.status,
				code: error.response?.data?.code,
				message: formatErrorMessage(error),
			},
		};
	}
}

// GET api/fmstasking/v1/mr/maintenance/workshops
export async function getWorkshops(search = "") {
	const res = await client("/v1/mr/maintenance/workshops", {
		method: "GET",
		version: "Tasking",
		params: { isActive: true, search: search || undefined },
	});
	return { data: res.data?.data ?? [] };
}

// PATCH api/fmstasking/v1/mr/issues/handover (EL-1206)
export async function setIssuesHandoverFlag(issueIds, flagged) {
	try {
		return await client("/v1/mr/issues/handover", {
			method: "PATCH",
			version: "Tasking",
			data: { issueIds, flagged },
		});
	} catch (error) {
		return {
			error: {
				status: error.response?.status,
				code: error.response?.data?.code,
				message: formatErrorMessage(error),
				rejected: error.response?.data?.rejected,
			},
		};
	}
}

const WORK_ORDER_NEXT_STEP = {
	Triage: "Add a job to get this started",
	InProgress: "Job in progress",
	InWorkshop: "Vehicle is with the workshop",
	CompletedByWorkshop: "This vehicle is ready to be collected",
	PendingCompletion: "Ready for sign-off",
};

export function toWorkOrderDetail(workOrder) {
	const { workshop, ...rest } = workOrder;
	return {
		...rest,
		destination: workshop
			? {
					type: "Workshop",
					workshopId: workshop.id,
					workshopName: workshop.name,
					address: workshop.address,
				}
			: null,
		nextStep: WORK_ORDER_NEXT_STEP[workOrder.status] ?? null,
	};
}

// GET api/fmstasking/v1/mr/work-orders/{id} (EL-1184)
// Unknown id (404 29201) resolves to no data; the view renders not-found.
export async function getWorkOrderById(workOrderId) {
	try {
		const res = await client(`/v1/mr/work-orders/${workOrderId}`, {
			method: "GET",
			version: "Tasking",
		});
		return { data: toWorkOrderDetail(res.data) };
	} catch (error: any) {
		if (error?.response?.data?.code === 29201) {
			return { data: undefined };
		}
		throw error;
	}
}

// PATCH api/fmstasking/v1/mr/work-orders/{id}/destination (EL-1209)
export async function updateWorkOrderDestination(workOrderId, { workshopId }) {
	let res;
	try {
		res = await client(`/v1/mr/work-orders/${workOrderId}/destination`, {
			method: "PATCH",
			version: "Tasking",
			data: { workshopId },
		});
	} catch (error) {
		return {
			error: {
				status: error.response?.status,
				code: error.response?.data?.code,
				message: formatErrorMessage(error),
			},
		};
	}
	const workshop = res.data?.workshop;
	const destination = {
		type: "Workshop",
		workshopId: workshop?.id ?? workshopId,
		workshopName: workshop?.name ?? null,
		address: workshop?.address ?? null,
	};
	return { data: { destination } };
}

const TERMINAL_WORK_ORDER_STATUSES = new Set(["Completed", "Cancelled"]);

const WORKSHOP_FLOW_TRANSITIONS = {
	Triage: ["InProgress", "InWorkshop", "Cancelled"],
	InProgress: ["InWorkshop", "PendingCompletion", "Cancelled"],
	InWorkshop: ["CompletedByWorkshop", "Cancelled"],
	CompletedByWorkshop: ["ReadyForCollection", "Cancelled"],
	ReadyForCollection: ["PendingCompletion", "Cancelled"],
	PendingCompletion: ["Completed", "Cancelled"],
	Completed: [],
	Cancelled: [],
};

const NONWORKSHOP_FLOW_TRANSITIONS = {
	Triage: ["InProgress", "Cancelled"],
	InProgress: ["PendingCompletion", "Cancelled"],
	PendingCompletion: ["Cancelled"],
	Completed: [],
	Cancelled: [],
};

function allowedTransitionsFor(status, flowType) {
	const map =
		flowType === "NonWorkshop"
			? NONWORKSHOP_FLOW_TRANSITIONS
			: WORKSHOP_FLOW_TRANSITIONS;
	return map[status] || [];
}

export async function updateWorkOrderStatus /* NOSONAR */(
	workOrderId,
	newStatus
) {
	const workOrder = mockWorkOrders.find(({ id }) => id === workOrderId);
	if (!workOrder) {
		return { data: { id: workOrderId, status: newStatus } };
	}

	if (newStatus === "InWorkshop") {
		const siblingOrders = Object.values(mockWorkOrdersByVehicle).find(
			(orders) => orders.includes(workOrder)
		);
		const conflicting = siblingOrders?.find(
			(order) => order.id !== workOrderId && order.status === "InWorkshop"
		);
		if (conflicting) {
			return {
				error: "WORKSHOP_CONFLICT",
				conflictingWorkOrder: conflicting,
			};
		}
	}

	// mockWorkOrderDetails is a separate object from the flat mockWorkOrders
	// row above — without this, Work Order Detail's own record (what
	// updateWorkOrderStatusTransition reads its currentState from) stays
	// stale after an in-flight transition here, so a later Mark as
	// completed/Cancel call sees a mismatched currentState and fails with
	// 409/29202.
	const workOrderDetail = mockWorkOrderDetails.find(
		({ id }) => id === workOrderId
	);
	const newAllowedTransitions = allowedTransitionsFor(
		newStatus,
		workOrderDetail?.flowType || "Workshop"
	);

	workOrder.status = newStatus;
	workOrder.allowedTransitions = newAllowedTransitions;

	if (workOrderDetail) {
		workOrderDetail.status = newStatus;
		workOrderDetail.allowedTransitions = newAllowedTransitions;
	}

	// The workshop landing page has its own dataset — an order handed off to
	// a workshop from here needs to actually appear there, not just in the
	// Ops-side records, and one already listed there has to follow along.
	const workshopListEntry = syncWorkshopListEntry(workOrderId, newStatus);
	if (newStatus === "InWorkshop" && !workshopListEntry) {
		if (workOrderDetail) {
			const vehicle = mockVehicles.find(
				(v) => v.vehicleId === workOrderDetail.vehicleId
			);
			const now = new Date().toISOString();
			mockWorkshopWorkOrders.push({
				id: workOrderDetail.id,
				code: workOrderDetail.code,
				plateNumber: workOrderDetail.plateNumber,
				makeAndModel: vehicle?.makeAndModel || "",
				status: newStatus,
				createdAt: workOrderDetail.createdAt,
				enteredWorkshopAt: now,
				appointmentStart: workOrderDetail.appointmentStart,
				updatedAt: now,
				issues: (workOrderDetail.issues || []).map((issue) => ({
					id: issue.id,
					code: issue.code,
					serviceCategory: issue.serviceCategory,
					issueType: issue.issueType,
					issueDescription: issue.issueDescription,
					outcome: issue.outcome ?? null,
					unfixedReason: issue.unfixedReason ?? null,
				})),
			});
		}
	}

	return {
		data: {
			id: workOrderId,
			status: newStatus,
			allowedTransitions: newAllowedTransitions,
		},
	};
}

// GET api/fmstasking/v1/mr/vehicles/{id}/activity-log (EL-1203)
export async function getVehicleActivityLog(
	vehicleId,
	params: {
		activityType?: "Issue" | "WorkOrder";
		sortDir?: "asc" | "desc";
		page?: number;
		size?: number;
	} = {}
) {
	const res = await client(`/v1/mr/vehicles/${vehicleId}/activity-log`, {
		method: "GET",
		version: "Tasking",
		params,
	});
	const body = res.data ?? {};
	return {
		data: {
			items: body.data ?? [],
			page: body.pageNumber,
			pageSize: body.pageSize,
			totalCount: body.totalCount ?? 0,
		},
	};
}

const WORKSHOP_SERVICE_CATEGORIES = new Set(["Bodywork", "Maintenance"]);

function issueKind(issue) {
	return WORKSHOP_SERVICE_CATEGORIES.has(issue.serviceCategory)
		? "Workshop"
		: "NonWorkshop";
}

function generateWorkOrderCode() {
	const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
	// Cosmetic mock code suffix, not a security token/id — unpredictability
	// and uniqueness aren't required here.
	const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase(); // NOSONAR
	return `WO${datePart}_${randomPart}`;
}

const NOT_GROUPABLE_ISSUE_STATUSES = new Set(["Cancelled", "Completed"]);

// Shared existence/vehicle-match/groupable-status checks for a set of Issue
// ids — used both by createWorkOrder itself and by moveIssueToNewWorkOrder,
// which must run these *before* detaching the issues from their current
// work order so a validation failure never leaves an issue half-moved.
function validateIssuesForGrouping(uniqueIssueIds, vehicleIdNum) {
	const issues = uniqueIssueIds.map((id) =>
		mockVehicleIssues.find((issue) => issue.id === id)
	);
	const missingIssueIds = uniqueIssueIds.filter(
		(id, index) => !issues[index]
	);
	if (missingIssueIds.length) {
		return {
			error: { status: 404, code: 29101, issueIds: missingIssueIds },
		};
	}

	const wrongVehicleIssueIds = issues
		.filter((issue) => issue.vehicleId !== vehicleIdNum)
		.map((issue) => issue.id);
	if (wrongVehicleIssueIds.length) {
		return {
			error: { status: 422, code: 29208, issueIds: wrongVehicleIssueIds },
		};
	}

	const notGroupableIssueIds = issues
		.filter((issue) => NOT_GROUPABLE_ISSUE_STATUSES.has(issue.status))
		.map((issue) => issue.id);
	if (notGroupableIssueIds.length) {
		return {
			error: { status: 409, code: 29210, issueIds: notGroupableIssueIds },
		};
	}

	const kinds = new Set(issues.map(issueKind));
	if (kinds.size !== 1) {
		return { error: { status: 422, code: 29205 } };
	}

	return { issues };
}

// POST api/fmstasking/v1/mr/work-orders (EL-1179)
export async function createWorkOrder({
	vehicleId,
	issueIds,
	flowType,
	destinationAddress = "",
	appointmentStart = "",
	appointmentEnd = "",
	issues = [],
}) {
	const optionalFields = Object.fromEntries(
		Object.entries({
			destinationAddress,
			appointmentStart,
			appointmentEnd,
		}).filter(([, value]) => value)
	);
	let res;
	try {
		res = await client("/v1/mr/work-orders", {
			method: "POST",
			version: "Tasking",
			data: { vehicleId, issueIds, flowType, ...optionalFields },
		});
	} catch (error) {
		return {
			error: {
				status: error.response?.status,
				code: error.response?.data?.code,
				message: formatErrorMessage(error),
			},
		};
	}
	const { id, code, createdAt } = res.data ?? {};
	addWorkOrderToMocks({
		id,
		code,
		createdAt,
		vehicleId,
		flowType,
		issues: issues.map((issue) => ({ ...issue, workOrder: { id, code } })),
	});
	return res;
}

function createMockWorkOrder({ vehicleId, issueIds = [], flowType }: any = {}) {
	// "Repeated ids... de-duplicated silently rather than rejected."
	const uniqueIssueIds = [...new Set(issueIds)];

	if (!uniqueIssueIds.length) {
		return { error: { status: 422, code: 29204 } };
	}

	const vehicleIdNum = Number(vehicleId);
	const validation = validateIssuesForGrouping(uniqueIssueIds, vehicleIdNum);
	if (validation.error) {
		return { error: validation.error };
	}
	const { issues } = validation;

	const alreadyAssignedIssues = issues.filter((issue) => issue.workOrder);
	if (alreadyAssignedIssues.length) {
		return {
			error: {
				status: 409,
				code: 29102,
				conflicts: alreadyAssignedIssues.map((issue) => ({
					issueId: issue.id,
					workOrderCode: issue.workOrder.code,
				})),
			},
		};
	}

	const [singleKind] = new Set(issues.map(issueKind));
	if (singleKind !== flowType) {
		return { error: { status: 422, code: 29205 } };
	}

	const id = Math.max(0, ...mockWorkOrders.map((o) => o.id)) + 1;
	const code = generateWorkOrderCode();
	const createdAt = new Date().toISOString();

	addWorkOrderToMocks({ id, code, createdAt, vehicleId, flowType, issues });
	issues.forEach((issue) => {
		issue.workOrder = { id, code };
	});

	return {
		data: { id, code, status: "Triage", createdAt },
	};
}

function removeById(list, id) {
	const index = list.findIndex((item) => item.id === id);
	if (index !== -1) list.splice(index, 1);
}

function addWorkOrderToMocks({
	id,
	code,
	createdAt,
	vehicleId,
	flowType,
	issues,
}) {
	const vehicleIdNum = Number(vehicleId);
	const allowedTransitions = allowedTransitionsFor("Triage", flowType);

	removeById(mockWorkOrders, id);
	removeById(mockWorkOrderDetails, id);
	Object.values(mockWorkOrdersByVehicle).forEach((orders) =>
		removeById(orders, id)
	);

	const newWorkOrder = {
		id,
		vehicleId: vehicleIdNum,
		code,
		issueCount: issues.length,
		jobCount: 0,
		handoverFlagged: false,
		createdAt,
		lastUpdatedAt: createdAt,
		status: "Triage",
		allowedTransitions,
		addedBy: "",
		workshop: null,
		completedByWorkshopAt: null,
	};

	if (!mockWorkOrdersByVehicle[vehicleIdNum]) {
		mockWorkOrdersByVehicle[vehicleIdNum] = [];
	}
	mockWorkOrdersByVehicle[vehicleIdNum].push(newWorkOrder);
	mockWorkOrders.push(newWorkOrder);

	const vehicle = mockVehicles.find((v) => v.vehicleId === vehicleIdNum);
	mockWorkOrderDetails.push({
		id,
		code,
		vehicleId: vehicleIdNum,
		plateNumber: vehicle?.plateNumber || "",
		status: "Triage",
		allowedTransitions,
		flowType,
		createdAt,
		lastUpdatedAt: createdAt,
		destination: null,
		appointmentStart: null,
		appointmentEnd: null,
		workshopEnteredAt: null,
		workshopCompletedAt: null,
		issues: issues as any,
		jobs: [],
	});
}

function removeIssueFromWorkOrder(issueId) {
	const vehicleIssue = mockVehicleIssues.find(({ id }) => id === issueId);
	if (!vehicleIssue?.workOrder) {
		return vehicleIssue;
	}

	const sourceWorkOrderId = vehicleIssue.workOrder.id;
	const sourceWorkOrder = mockWorkOrderDetails.find(
		({ id }) => id === sourceWorkOrderId
	);
	if (sourceWorkOrder) {
		sourceWorkOrder.issues = (sourceWorkOrder.issues || []).filter(
			(issue) => issue.id !== issueId
		);
	}

	const sourceFlatWorkOrder = mockWorkOrders.find(
		({ id }) => id === sourceWorkOrderId
	);
	if (sourceFlatWorkOrder) {
		sourceFlatWorkOrder.issueCount = Math.max(
			0,
			(sourceFlatWorkOrder.issueCount || 0) - 1
		);
	}

	// A third dataset (see CLAUDE.md) — the workshop landing page's own list
	// of work orders each carries its own copy of the issues it shows.
	const sourceWorkshopListEntry = mockWorkshopWorkOrders.find(
		({ id }) => id === sourceWorkOrderId
	);
	if (sourceWorkshopListEntry) {
		sourceWorkshopListEntry.issues = sourceWorkshopListEntry.issues.filter(
			(issue) => issue.id !== issueId
		);
	}

	vehicleIssue.workOrder = null;
	return vehicleIssue;
}

export async function moveIssueToBacklog(issueId) /* NOSONAR */ {
	const vehicleIssue = removeIssueFromWorkOrder(issueId);
	if (!vehicleIssue) {
		return { error: { status: 404, code: 29201 } };
	}

	vehicleIssue.status = "Backlog";
	vehicleIssue.allowedTransitions = [];
	return { data: { id: issueId, status: "Backlog", allowedTransitions: [] } };
}

export async function moveIssueToNewWorkOrder( // NOSONAR
	issueIds,
	{ vehicleId }: any = {}
) {
	const ids = Array.isArray(issueIds) ? issueIds : [issueIds];
	const uniqueIds = [...new Set(ids)];
	if (!uniqueIds.length) {
		return { error: { status: 404, code: 29201 } };
	}

	const vehicleIdNum = Number(vehicleId);
	const validation = validateIssuesForGrouping(uniqueIds, vehicleIdNum);
	if (validation.error) {
		return { error: validation.error };
	}
	const { issues } = validation;

	const [flowType] = new Set(issues.map(issueKind));
	uniqueIds.forEach((id) => removeIssueFromWorkOrder(id));

	return createMockWorkOrder({ vehicleId, issueIds: uniqueIds, flowType });
}

export async function moveIssueToExistingWorkOrder( // NOSONAR
	issueIds,
	targetWorkOrderId
) {
	const ids = [...new Set(Array.isArray(issueIds) ? issueIds : [issueIds])];
	const vehicleIssues = ids
		.map((id) => mockVehicleIssues.find((issue) => issue.id === id))
		.filter(Boolean);

	if (vehicleIssues.length !== ids.length) {
		return { error: { status: 404, code: 29201 } };
	}

	const targetWorkOrder = mockWorkOrderDetails.find(
		({ id }) => id === Number(targetWorkOrderId)
	);
	if (!targetWorkOrder) {
		return { error: { status: 404, code: 29201 } };
	}

	if (TERMINAL_WORK_ORDER_STATUSES.has(targetWorkOrder.status)) {
		return { error: { status: 409, code: 29202 } };
	}

	const notGroupableIssueIds = vehicleIssues
		.filter((issue) => NOT_GROUPABLE_ISSUE_STATUSES.has(issue.status))
		.map((issue) => issue.id);
	if (notGroupableIssueIds.length) {
		return {
			error: { status: 409, code: 29210, issueIds: notGroupableIssueIds },
		};
	}

	const wrongVehicleIssueIds = vehicleIssues
		.filter((issue) => issue.vehicleId !== targetWorkOrder.vehicleId)
		.map((issue) => issue.id);
	if (wrongVehicleIssueIds.length) {
		return {
			error: { status: 422, code: 29208, issueIds: wrongVehicleIssueIds },
		};
	}

	const wrongKindIssueIds = vehicleIssues
		.filter((issue) => issueKind(issue) !== targetWorkOrder.flowType)
		.map((issue) => issue.id);
	if (wrongKindIssueIds.length) {
		return {
			error: { status: 422, code: 29205, issueIds: wrongKindIssueIds },
		};
	}

	ids.forEach((id) => removeIssueFromWorkOrder(id));
	attachIssuesToMockWorkOrder(targetWorkOrder, vehicleIssues);

	return {
		data: {
			ids: vehicleIssues.map((issue) => issue.id),
			workOrder: { id: targetWorkOrder.id, code: targetWorkOrder.code },
		},
	};
}

function attachIssuesToMockWorkOrder(targetWorkOrder, issues) {
	targetWorkOrder.issues = [
		...(targetWorkOrder.issues || []),
		...(issues as any),
	];

	const targetFlatWorkOrder = mockWorkOrders.find(
		({ id }) => id === targetWorkOrder.id
	);
	if (targetFlatWorkOrder) {
		targetFlatWorkOrder.issueCount =
			(targetFlatWorkOrder.issueCount || 0) + issues.length;
	}

	const targetWorkshopListEntry = mockWorkshopWorkOrders.find(
		({ id }) => id === targetWorkOrder.id
	);
	if (targetWorkshopListEntry) {
		targetWorkshopListEntry.issues = [
			...targetWorkshopListEntry.issues,
			...issues.map((issue) => ({
				id: issue.id,
				code: issue.code,
				serviceCategory: issue.serviceCategory,
				issueType: issue.issueType,
				issueDescription: issue.issueDescription,
				outcome: issue.outcome ?? null,
				unfixedReason: issue.unfixedReason ?? null,
			})),
		];
	}

	issues.forEach((issue) => {
		issue.workOrder = {
			id: targetWorkOrder.id,
			code: targetWorkOrder.code,
		};
	});
}

// POST api/fmstasking/v1/mr/work-orders/{id}/issues (EL-1180)
export async function addIssuesToWorkOrder(
	workOrderId,
	{
		issueIds,
		addedVia,
		issues = [],
	}: {
		issueIds: number[];
		addedVia:
			| "Grouping"
			| "ShiftManagerEscalation"
			| "WorkshopReported"
			| "Regrouped";
		issues?: any[];
	}
) {
	let res;
	try {
		res = await client(`/v1/mr/work-orders/${workOrderId}/issues`, {
			method: "POST",
			version: "Tasking",
			data: { issueIds, addedVia },
		});
	} catch (error) {
		return {
			error: {
				status: error.response?.status,
				code: error.response?.data?.code,
				message: formatErrorMessage(error),
			},
		};
	}
	// Single add and bulk share this endpoint; bulk can partly succeed.
	const body = res.data ?? {};
	const added = body.added ?? [];

	// Keeps the still-mocked work order pages in sync.
	const targetWorkOrder = mockWorkOrderDetails.find(
		({ id }) => id === Number(workOrderId)
	);
	if (targetWorkOrder) {
		attachIssuesToMockWorkOrder(
			targetWorkOrder,
			issues
				.filter((issue) => added.includes(issue.id))
				.map((issue) => ({ ...issue }))
		);
	}

	return {
		data: {
			added,
			rejected: body.rejected ?? [],
			workOrderStatus: body.workOrderStatus ?? null,
			workOrder: { id: Number(workOrderId) },
		},
	};
}

// DELETE api/fmstasking/v1/mr/work-orders/{id}/issues/{issueId} (EL-1180)
// The issue goes back to Backlog; removing the last one is rejected (29204).
export async function deleteWorkOrderIssue(workOrderId, issueId) {
	try {
		await client(`/v1/mr/work-orders/${workOrderId}/issues/${issueId}`, {
			method: "DELETE",
			version: "Tasking",
		});
	} catch (error) {
		return {
			error: {
				status: error.response?.status,
				code: error.response?.data?.code,
				message: formatErrorMessage(error),
			},
		};
	}
	detachIssueFromMockWorkOrder(Number(workOrderId), Number(issueId));
	return { data: { workOrderId: Number(workOrderId), issueId } };
}

// Keeps the still-mocked work order pages in sync.
function detachIssueFromMockWorkOrder(workOrderId, issueId) {
	const detail = mockWorkOrderDetails.find(({ id }) => id === workOrderId);
	if (detail) {
		detail.issues = (detail.issues || []).filter(
			(issue) => issue.id !== issueId
		);
	}
	const flat = mockWorkOrders.find(({ id }) => id === workOrderId);
	if (flat) {
		flat.issueCount = Math.max(0, (flat.issueCount || 0) - 1);
	}
	const workshopEntry = mockWorkshopWorkOrders.find(
		({ id }) => id === workOrderId
	);
	if (workshopEntry) {
		workshopEntry.issues = workshopEntry.issues.filter(
			(issue) => issue.id !== issueId
		);
	}
}

function issueAllowedTransitionsFor(status) {
	return status === "InProgress" ? ["Completed", "Cancelled"] : [];
}

const JOB_FLOW_TRANSITIONS = {
	open: ["assigned", "cancelled"],
	assigned: ["ongoing", "cancelled"],
	ongoing: ["paused", "completed", "cancelled"],
	paused: ["ongoing", "cancelled"],
	completed: [],
	incomplete: [],
	cancelled: [],
};

function jobAllowedTransitionsFor(status) {
	return JOB_FLOW_TRANSITIONS[status] || [];
}

// Jobs aren't an M&R resource — fms-tasking owns them, and FMS web is meant
// to call its endpoints directly rather than have M&R wrap/duplicate them.
function findMockJob(jobId) {
	for (const workOrder of mockWorkOrderDetails) {
		const job = (workOrder.jobs || []).find((j) => j.jobId === jobId);
		if (job) return job;
	}
	return undefined;
}

function applyJobState(
	jobId,
	newStatus,
	{ reasonCode, notes }: { reasonCode?: string; notes?: string } = {}
) {
	const job = findMockJob(jobId) as any;
	if (!job) {
		return { data: { id: jobId, status: newStatus } };
	}

	const newAllowedTransitions = jobAllowedTransitionsFor(newStatus);
	job.status = newStatus;
	job.allowedTransitions = newAllowedTransitions;
	if (reasonCode !== undefined) job.cancelReasonCode = reasonCode;
	if (notes !== undefined) job.cancelNotes = notes;

	return {
		data: {
			id: jobId,
			status: newStatus,
			allowedTransitions: newAllowedTransitions,
			cancelReasonCode: job.cancelReasonCode,
			cancelNotes: job.cancelNotes,
		},
	};
}

export async function assignJob(jobId) /* NOSONAR */ {
	return applyJobState(jobId, "assigned");
}

export async function markJobOngoing(jobId) /* NOSONAR */ {
	return applyJobState(jobId, "ongoing");
}

export async function pauseJob(jobId) /* NOSONAR */ {
	return applyJobState(jobId, "paused");
}

export async function completeJob(jobId) /* NOSONAR */ {
	return applyJobState(jobId, "completed");
}

export async function cancelJob(jobId, extra = {}) /* NOSONAR */ {
	return applyJobState(jobId, "cancelled", extra);
}

const JOB_STATE_ACTIONS = {
	assigned: assignJob,
	ongoing: markJobOngoing,
	paused: pauseJob,
	completed: completeJob,
	cancelled: cancelJob,
};

// Dispatches to the right per-state mock above — a stand-in for FMS web
// picking the right real PATCH route once those exist. Not itself a real
// endpoint.
export async function updateJobStatus /* NOSONAR */(
	jobId,
	newStatus,
	extra = {}
) {
	const action = JOB_STATE_ACTIONS[newStatus];
	if (!action) {
		return { data: { id: jobId, status: newStatus } };
	}
	return action(jobId, extra);
}

function generateJobCode() {
	const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
	// Cosmetic mock code suffix, not a security token/id — unpredictability
	// and uniqueness aren't required here.
	const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase(); // NOSONAR
	return { datePart, code: `J${datePart}_${randomPart}`, randomPart };
}

let mockTaskIdSeq = 950000;

// Stands in for FMS web calling `POST api/operator/fmstasking/v1/jobs`
// directly (to create the job + its tasks) and then
// `POST api/fmstasking/v1/mr/work-orders/{id}/jobs` to map the result to
// this work order — see updateJobStatus above for why this isn't an M&R
// endpoint. Not itself a real call, just mock scaffolding until EL-1215/A-53
// land.
export async function createWorkOrderJob(workOrderId, jobData) /* NOSONAR */ {
	const workOrder = mockWorkOrderDetails.find(
		({ id }) => id === Number(workOrderId)
	);
	if (!workOrder) {
		return { error: { status: 404, code: 29201 } };
	}

	const { code, datePart } = generateJobCode();
	const tasks = (jobData.tasks || []).map((task) => ({
		taskId: mockTaskIdSeq++,
		taskPresetId: null,
		code: `TK${datePart}_${Math.random() // NOSONAR
			.toString(36)
			.slice(2, 8)
			.toUpperCase()}`,
		name: task.name,
		plannerRemarks: task.plannerRemarks,
		priority: task.priority,
		status: "open",
		media: [],
	}));

	const newJob = {
		jobId: mockTaskIdSeq++,
		code,
		status: "open",
		allowedTransitions: jobAllowedTransitionsFor("open"),
		priority: jobData.priority,
		start: jobData.start,
		due: jobData.due,
		assignedTo: jobData.assignedTo,
		createdBy: jobData.createdBy,
		// Mirrors JobManagement/Assign.svelte's createBlackout — not acted on
		// by this mock (no blackout record exists here to create), just
		// carried through on the job the same way the real POST /v1/Jobs
		// payload does.
		createBlackout: jobData.createBlackout,
		taskCount: tasks.length,
		tasks,
	};

	workOrder.jobs = [...(workOrder.jobs || []), newJob];

	return { data: newJob };
}

export async function updateIssueStatus(issueId, newStatus) /* NOSONAR */ {
	const vehicleIssue = mockVehicleIssues.find(({ id }) => id === issueId);
	if (!vehicleIssue) {
		return { data: { id: issueId, status: newStatus } };
	}

	const newAllowedTransitions = issueAllowedTransitionsFor(newStatus);
	vehicleIssue.status = newStatus;
	vehicleIssue.allowedTransitions = newAllowedTransitions;

	if (vehicleIssue.workOrder) {
		const workOrder = mockWorkOrderDetails.find(
			({ id }) => id === vehicleIssue.workOrder.id
		);
		const nestedIssue = workOrder?.issues.find(
			(issue) => issue.id === issueId
		);
		if (nestedIssue) {
			nestedIssue.status = newStatus;
			nestedIssue.allowedTransitions = newAllowedTransitions;
		}
	}

	return {
		data: {
			id: issueId,
			status: newStatus,
			allowedTransitions: newAllowedTransitions,
		},
	};
}

function isJobOpen(job) {
	return !["completed", "incomplete", "cancelled"].includes(job.status);
}

function syncFlatWorkOrderStatus(workOrderId, newStatus, allowedTransitions) {
	const flatWorkOrder = mockWorkOrders.find(({ id }) => id === workOrderId);
	const detailWorkOrder = mockWorkOrderDetails.find(
		({ id }) => id === workOrderId
	);
	if (flatWorkOrder) {
		flatWorkOrder.status = newStatus;
		flatWorkOrder.allowedTransitions = allowedTransitions;
		flatWorkOrder.lastUpdatedAt =
			detailWorkOrder?.lastUpdatedAt || flatWorkOrder.lastUpdatedAt;
	}
}

function syncWorkshopListEntry(
	workOrderId,
	newStatus,
	{
		updatedAt = new Date().toISOString(),
		removedIssueIds = [],
	}: { updatedAt?: string; removedIssueIds?: number[] } = {}
) {
	const workshopListEntry = mockWorkshopWorkOrders.find(
		({ id }) => id === workOrderId
	);
	if (workshopListEntry) {
		workshopListEntry.status = newStatus;
		workshopListEntry.updatedAt = updatedAt;
		workshopListEntry.issues = workshopListEntry.issues.filter(
			(issue) => !removedIssueIds.includes(issue.id)
		);
	}
	return workshopListEntry;
}

// PATCH api/fmstasking/v1/mr/work-orders/{id}/status (EL-1181)
function completeWorkOrderTransition(
	workOrder,
	{
		currentState,
		forceCancelOpenJobs,
		setVehicleActive,
		reasonCode,
		fixedIssueIds,
	}
) {
	if (currentState !== "PendingCompletion") {
		return {
			error: { status: 409, code: 29202, currentState: workOrder.status },
		};
	}
	if (reasonCode) {
		return { error: { status: 422, code: 29051 } };
	}
	if (setVehicleActive === undefined || setVehicleActive === null) {
		return { error: { status: 422, code: 29051 } };
	}

	const openJobs = (workOrder.jobs || []).filter(isJobOpen);
	if (openJobs.length > 0 && !forceCancelOpenJobs) {
		return { error: { status: 409, code: 29203 } };
	}

	const completedIssueIds = [];
	const returnedIssueIds = [];
	(workOrder.issues || []).forEach((issue) => {
		// An explicit fixedIssueIds list overrides the stored outcome.
		const isFixed = fixedIssueIds
			? fixedIssueIds.includes(issue.id)
			: issue.outcome !== "NotFixed";
		if (isFixed) {
			completedIssueIds.push(issue.id);
		} else {
			returnedIssueIds.push(issue.id);
		}
	});

	const cancelledJobIds = openJobs.map((job) => job.jobId);
	const completedAt = new Date().toISOString();
	const newStatus = "Completed";

	workOrder.status = newStatus;
	workOrder.allowedTransitions = [];
	workOrder.lastUpdatedAt = completedAt;
	(workOrder.jobs || []).forEach((job) => {
		if (cancelledJobIds.includes(job.jobId)) job.status = "cancelled";
	});
	returnedIssueIds.forEach((issueId) => {
		const fullIssue = mockVehicleIssues.find((i) => i.id === issueId);
		if (fullIssue) {
			fullIssue.workOrder = null;
			fullIssue.status = "Backlog";
			// Same as moveIssueToBacklog — a Backlog issue has nowhere to go.
			fullIssue.allowedTransitions = [];
		}
	});
	completedIssueIds.forEach((issueId) => {
		const fullIssue = mockVehicleIssues.find((i) => i.id === issueId);
		if (fullIssue) fullIssue.status = "Completed";
	});

	(workOrder.issues || []).forEach((issue) => {
		if (completedIssueIds.includes(issue.id)) {
			issue.status = "Completed";
		}
	});
	workOrder.issues = (workOrder.issues || []).filter(
		(issue) => !returnedIssueIds.includes(issue.id)
	);

	const flatWorkOrderForCount = mockWorkOrders.find(
		({ id }) => id === workOrder.id
	);
	if (flatWorkOrderForCount) {
		flatWorkOrderForCount.issueCount = Math.max(
			0,
			(flatWorkOrderForCount.issueCount || 0) - returnedIssueIds.length
		);
	}

	if (setVehicleActive) {
		const vehicle = mockVehicles.find(
			(v) => v.vehicleId === workOrder.vehicleId
		);
		if (vehicle) {
			vehicle.vehicleStatus = "Active";
			vehicle.enteredWorkshopAt = null;
			vehicle.readyForCollection = null;
		}
	}

	syncFlatWorkOrderStatus(workOrder.id, newStatus, []);
	syncWorkshopListEntry(workOrder.id, newStatus, {
		updatedAt: completedAt,
		removedIssueIds: returnedIssueIds,
	});

	return {
		data: {
			id: workOrder.id,
			status: newStatus,
			allowedTransitions: [],
			completedAt,
			completedBy: "opsmgr@getgo.sg",
			completedIssueIds,
			returnedIssueIds,
			cancelledJobIds,
			blackoutReleased: true,
			vehicleReactivated: !!setVehicleActive,
		},
	};
}

function cancelWorkOrderTransition(
	workOrder,
	{ forceCancelOpenJobs, setVehicleActive, reasonCode, notes }
) {
	if (setVehicleActive !== undefined && setVehicleActive !== null) {
		return { error: { status: 422, code: 29051 } };
	}
	if (!reasonCode) {
		return { error: { status: 422, code: 29051 } };
	}

	if ((workOrder.issues || []).length > 0) {
		return { error: { status: 409, code: 29406 } };
	}

	const openJobs = (workOrder.jobs || []).filter(isJobOpen);
	if (openJobs.length > 0 && !forceCancelOpenJobs) {
		return { error: { status: 409, code: 29203 } };
	}

	const movedOutIssueIds = (workOrder.issues || []).map((issue) => issue.id);
	const cancelledJobIds = openJobs.map((job) => job.jobId);
	const cancelAt = new Date().toISOString();

	workOrder.status = "Cancelled";
	workOrder.allowedTransitions = [];
	workOrder.lastUpdatedAt = cancelAt;
	(workOrder as any).cancelReasonCode = reasonCode;
	(workOrder as any).cancelNotes = notes;
	(workOrder.jobs || []).forEach((job) => {
		if (cancelledJobIds.includes(job.jobId)) job.status = "cancelled";
	});

	syncFlatWorkOrderStatus(workOrder.id, "Cancelled", []);
	syncWorkshopListEntry(workOrder.id, "Cancelled", { updatedAt: cancelAt });

	return {
		data: {
			id: workOrder.id,
			status: "Cancelled",
			allowedTransitions: [],
			cancelReasonCode: reasonCode,
			cancelNotes: notes,
			cancelAt,
			movedOutIssueIds,
			cancelledJobIds,
			blackoutReleased: true,
		},
	};
}

export async function updateWorkOrderStatusTransition( // NOSONAR
	workOrderId,
	{
		currentState,
		targetState,
		forceCancelOpenJobs = false,
		setVehicleActive = undefined,
		reasonCode = null,
		notes = "",
		fixedIssueIds = undefined,
	}: any = {}
) {
	const workOrder = mockWorkOrderDetails.find(
		({ id }) => id === Number(workOrderId)
	);
	if (!workOrder) {
		return { error: { status: 404, code: 29201 } };
	}

	if (targetState === "Completed" && workOrder.flowType === "NonWorkshop") {
		return {
			error: { status: 409, code: 29202, currentState: workOrder.status },
		};
	}

	if (workOrder.status !== currentState) {
		return {
			error: { status: 409, code: 29202, currentState: workOrder.status },
		};
	}

	if (targetState === "Completed") {
		return completeWorkOrderTransition(workOrder, {
			currentState,
			forceCancelOpenJobs,
			setVehicleActive,
			reasonCode,
			fixedIssueIds,
		});
	}

	if (targetState === "Cancelled") {
		return cancelWorkOrderTransition(workOrder, {
			forceCancelOpenJobs,
			setVehicleActive,
			reasonCode,
			notes,
		});
	}

	return {
		error: { status: 409, code: 29202, currentState: workOrder.status },
	};
}
